import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

function AgeCalculator() {
  const [dob, setDob] = useState("");
  const [age, setAge] = useState(null);
  const [error, setError] = useState("");
  const [funFact, setFunFact] = useState("");
  const [showFunFact, setShowFunFact] = useState(false);

  const calculateAge = () => {
    if (!dob) {
      setError("Please select a valid date of birth.");
      setAge(null);
      setShowFunFact(false);
      return;
    }

    const birthDate = new Date(dob);
    const today = new Date();
    if (birthDate > today) {
      setError("Please select a valid date of birth.");
      setAge(null);
      setShowFunFact(false);
      return;
    }

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    setError("");
    setAge({ years, months, days });

    const totalDays = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.floor(totalDays / 7);
    const birthDayName = birthDate.toLocaleDateString("en-US", { weekday: "long" });
    const leapYears = Math.floor(years / 4);

    const funFactsList = [
      `You were born on a ${birthDayName}, which means you’ve lived for about ${totalDays.toLocaleString()} days or ${totalWeeks.toLocaleString()} weeks!`,
      `You’ve celebrated around ${years} birthdays so far — and ${Math.floor(years / 10)} of them were milestone decades! 🎉`,
      `You’ve been alive for approximately ${(totalDays * 24).toLocaleString()} hours!`,
      `You have lived through about ${leapYears} leap years. That’s ${leapYears} extra days of life!`,
      `If you stacked every day of your life as a coin, it would reach the height of about ${(totalDays * 0.07).toFixed(1)} meters! 🪙`,
    ];

    setFunFact(funFactsList[Math.floor(Math.random() * funFactsList.length)]);
    setShowFunFact(true);
  };

  return (
    <CalculatorPage
      title="Age Calculator"
      metaDescription="Free age calculator: find your exact age in years, months, and days from your date of birth, plus fun facts about your time alive."
      intro={
        <>
          An <strong>age calculator</strong> determines your exact age in
          years, months, and days based on your date of birth.
        </>
      }
      seoContent={
        <>
          <h2>How your exact age is calculated</h2>
          <p>
            Your age isn't just the current year minus your birth year — the
            months and days matter. This calculator counts full years first,
            then full months since your last birthday, then the remaining
            days, borrowing from the previous month when needed (the same way
            you'd do subtraction by hand).
          </p>
          <h2>Common uses</h2>
          <p>
            Exact age in years, months, and days is often needed for school
            admission forms, job applications, visa paperwork, and pension or
            insurance calculations — situations where "about 25" isn't precise
            enough. It's also handy for working out age differences or
            celebrating half-birthdays and day-1000 milestones.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>How is age calculated in years, months, and days?</h3>
          <p>
            The calculator counts complete years since your birth date, then
            the whole months since your last birthday, then the leftover days —
            borrowing from the previous month where needed, just like manual
            subtraction.
          </p>
          <h3>Does it account for leap years?</h3>
          <p>
            Yes. Because it works from the actual calendar dates, February 29
            birthdays and leap years are handled correctly.
          </p>
          <h3>Can I use it to find the age difference between two people?</h3>
          <p>
            This tool measures age from a birth date to today. To compare two
            people, calculate each person's age and subtract, or use the Days
            Between Dates calculator for an exact gap.
          </p>
        </>
      }
    >
      <div className="fields">
        <label className="field">
          <span>Date of Birth</span>
          <input
            type="date"
            value={dob}
            onChange={(e) => {
              setDob(e.target.value);
              setShowFunFact(false);
              setAge(null);
              setError("");
            }}
          />
        </label>
      </div>

      <button className="btn" onClick={calculateAge}>Calculate Age</button>

      {error && <div className="error">{error}</div>}

      {age && (
        <>
          <div className="result">
            You are <strong>{age.years}</strong> years,{" "}
            <strong>{age.months}</strong> months, and{" "}
            <strong>{age.days}</strong> days old.
          </div>
          {showFunFact && (
            <div className="fun-fact fun-fact-animate">💡 {funFact}</div>
          )}
        </>
      )}
    </CalculatorPage>
  );
}

export default AgeCalculator;
